import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * Australian suburb autocomplete via Nominatim (OpenStreetMap) proxy at /api/geo/suburbs.
 * Debounced search, dropdown of matches, formats as "Suburb STATE" (STATE abbreviation).
 */
export default function SuburbInput({ value, onChange, onCommit, className = "", placeholder, "data-testid": testId, ...rest }) {
  const [q, setQ] = useState(value || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const boxRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    if (!open || (q || "").trim().length < 2) { setOpts([]); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/geo/suburbs", { params: { q } });
        setOpts(Array.isArray(data) ? data : []);
        setHi(-1);
      } catch {
        setOpts([]);
      }
    }, 280);
    return () => timerRef.current && clearTimeout(timerRef.current);
    // `api` is a stable module import; `setOpts`/`setHi` are React state
    // setters (guaranteed stable) — none belong in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  // outside click to close
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (o) => {
    setQ(o.label);
    setOpts([]);
    setOpen(false);
    onChange && onChange(o.label);
    onCommit && onCommit(o.label);
  };

  const onKey = (e) => {
    if (!open || opts.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(opts.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(opts[hi]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); onChange && onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { setTimeout(() => onCommit && onCommit(q), 220); }}
        onKeyDown={onKey}
        className={className}
        placeholder={placeholder || "Start typing a suburb…"}
        data-testid={testId}
        autoComplete="off"
        {...rest}
      />
      {open && opts.length > 0 && (
        <ul
          className="absolute left-0 top-full z-50 w-full bg-paper border border-ink/20 mt-1 max-h-64 overflow-auto shadow-[0_0_0_1px_rgba(14,17,19,0.04)]"
          data-testid={testId ? `${testId}-options` : "suburb-options"}
        >
          {opts.map((o, i) => (
            <li key={o.label + i}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                className={`w-full text-left px-3 py-2 mono-label transition-colors ${i === hi ? "bg-bone text-marine" : "hover:bg-bone"}`}
                data-testid={testId ? `${testId}-option-${i}` : `suburb-option-${i}`}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
